import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { crm, type ContactRow, type OrganizationRow } from '@/lib/crm-client';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useDebouncedSearch } from '@/lib/useDebouncedSearch';

/**
 * Auswahl einer Firma oder eines Kontakts.
 *
 * Ersetzt die `<select>`-Listen, die bisher alle Firmen und Kontakte am Stück
 * enthielten. Bei 60 Einträgen ging das noch; bei einigen hundert scrollt man
 * an dem vorbei, was man sucht.
 *
 * Gesucht wird auf dem Server, nicht im Browser: die vollständige Liste soll
 * gar nicht erst geladen werden. Ohne Eingabe kommen die zuletzt geänderten —
 * das ist fast immer der Eintrag, den man gerade meint.
 */
interface Treffer {
  id: string;
  label: string;
  hint: string | null;
}

interface Props {
  kind: 'organization' | 'contact';
  value: string | null;
  onChange: (id: string | null) => void;
  /** Schränkt die Suche auf eine Zielgruppe ein. */
  audienceId?: string;
  placeholder?: string;
  /** Erlaubt „keine Auswahl“. Standard: ja. */
  allowEmpty?: boolean;
  disabled?: boolean;
  id?: string;
}

const LIMIT = 20;

export function EntityPicker({
  kind,
  value,
  onChange,
  audienceId,
  placeholder,
  allowEmpty = true,
  disabled,
  id,
}: Props) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<Treffer | null>(null);
  const [markiert, setMarkiert] = useState(0);

  const huelle = useRef<HTMLDivElement>(null);
  const feld = useRef<HTMLInputElement>(null);

  const laden = async (begriff: string): Promise<Treffer[]> => {
    const args = {
      limit: LIMIT,
      ...(audienceId ? { audienceId } : {}),
      ...(begriff.trim() ? { search: begriff.trim() } : {}),
    };
    if (kind === 'organization') {
      const rows = (await crm.organizations.list(args)) as OrganizationRow[];
      return rows.map((o) => ({
        id: o.id,
        label: o.name,
        hint: [o.city, o.isCustomer ? 'Kunde' : null].filter(Boolean).join(' · ') || null,
      }));
    }
    const rows = (await crm.contacts.list(args)) as ContactRow[];
    return rows.map((c) => ({
      id: c.id,
      label: c.displayName,
      hint: [c.organizationName, c.email].filter(Boolean).join(' · ') || null,
    }));
  };

  // Nur wenn geöffnet gesucht — sonst suchte jeder Picker auf der Seite im
  // Hintergrund mit. Die Wartezeit, das Laden-Flag und das Verwerfen einer
  // überholten Antwort übernimmt der Hook.
  const {
    data,
    loading: laedt,
    error: sucheFehler,
  } = useDebouncedSearch(
    () => laden(suche),
    [suche, audienceId, kind],
    { enabled: offen },
  );
  const treffer = data ?? [];

  useEffect(() => {
    setMarkiert(0);
  }, [data]);

  // Die aktuelle Auswahl muss beschriftet werden, auch wenn sie nicht unter
  // den ersten 20 Treffern steht.
  useEffect(() => {
    let abgebrochen = false;
    if (!value) {
      setGewaehlt(null);
      return;
    }
    const holen = async () => {
      const eintrag =
        kind === 'organization'
          ? await crm.organizations.get(value)
          : await crm.contacts.get(value);
      if (abgebrochen || !eintrag) return;
      setGewaehlt(
        kind === 'organization'
          ? {
              id: eintrag.id,
              label: (eintrag as OrganizationRow).name,
              hint: (eintrag as OrganizationRow).city ?? null,
            }
          : {
              id: eintrag.id,
              label: (eintrag as ContactRow).displayName,
              hint: (eintrag as ContactRow).organizationName ?? null,
            },
      );
    };
    void holen();
    return () => {
      abgebrochen = true;
    };
  }, [value, kind]);

  useEffect(() => {
    if (offen) feld.current?.focus();
  }, [offen]);

  // Klick nach draußen schließt. Ohne das bliebe die Liste offen, während man
  // im Formular weiterarbeitet.
  useEffect(() => {
    if (!offen) return;
    const aufKlick = (e: MouseEvent) => {
      if (!huelle.current?.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener('mousedown', aufKlick);
    return () => document.removeEventListener('mousedown', aufKlick);
  }, [offen]);

  const waehlen = (t: Treffer | null) => {
    onChange(t?.id ?? null);
    setGewaehlt(t);
    setOffen(false);
    setSuche('');
  };

  const aufTaste = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMarkiert((i) => Math.min(i + 1, treffer.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMarkiert((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const t = treffer[markiert];
      if (t) waehlen(t);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Ohne das liest auch der Host-Dialog dasselbe Escape und schließt
      // sich gleich mit — man wollte nur das Dropdown zu.
      e.stopPropagation();
      setOffen(false);
    }
  };

  const beschriftung =
    gewaehlt?.label ??
    placeholder ??
    (kind === 'organization' ? 'Firma wählen' : 'Kontakt wählen');

  return (
    <div ref={huelle} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOffen((o) => !o)}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-left text-sm shadow-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
          !gewaehlt && 'text-muted-foreground',
        )}
      >
        <span className="truncate">{beschriftung}</span>
        <span className="flex shrink-0 items-center gap-1">
          {allowEmpty && gewaehlt && (
            <X
              className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
              aria-label="Auswahl aufheben"
              onClick={(e) => {
                e.stopPropagation();
                waehlen(null);
              }}
            />
          )}
          <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
        </span>
      </button>

      {offen && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-border bg-card shadow-lg">
          <div className="flex items-center gap-2 border-b border-border px-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Input
              ref={feld}
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              onKeyDown={aufTaste}
              placeholder="Suchen …"
              className="h-9 border-0 shadow-none focus-visible:ring-0"
            />
          </div>

          <ul className="max-h-64 overflow-auto scrollbar-thin py-1">
            {laedt && (
              <li className="px-3 py-2 text-sm text-muted-foreground">Lade …</li>
            )}
            {!laedt && sucheFehler && (
              <li className="px-3 py-2 text-sm text-destructive">{sucheFehler}</li>
            )}
            {!laedt && !sucheFehler && treffer.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted-foreground">
                Nichts gefunden.
              </li>
            )}
            {!laedt &&
              !sucheFehler &&
              treffer.map((t, i) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setMarkiert(i)}
                    onClick={() => waehlen(t)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm',
                      i === markiert ? 'bg-accent text-accent-foreground' : '',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{t.label}</span>
                      {t.hint && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {t.hint}
                        </span>
                      )}
                    </span>
                    {t.id === value && <Check className="h-3.5 w-3.5 shrink-0" />}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
